import { EVENT_TYPE, EVENT_TYPE_META, displayEventType } from './eventTypes.js';
import { INTERIM_EVENT_TYPE } from './interimEventTypes.js';
import { humanizeReason } from '../utils/textCase.js';
import {
  TYPE_FILTER_CUSTOMER,
  CUSTOMER_WIRE_TYPES,
} from '../utils/customerClassification.js';

/**
 * Filter option sources for the inbox toolbar. Mock-driven for V1 — the real
 * option sets come from the Work Queue API once CT-4867 lands.
 */

/** Wire types from fan-out that must not appear in the Event Type filter (not in prototype V1). */
export const TYPE_FILTER_EXCLUDED_WIRE_TYPES = new Set([
  INTERIM_EVENT_TYPE.PROPOSAL,
  INTERIM_EVENT_TYPE.TASK,
]);

/**
 * Event Type filter options built from the REAL types present in the queue
 * (`typeCounts` from useEventList), each with its count — the static
 * {@link TYPE_OPTIONS} below is the mock prototype set and never matched the
 * interim fan-out types (change_request, claim_suggestion, proposal, task).
 *
 * Labels/icons come from EVENT_TYPE_META on the **prototype display type**
 * ({@link displayEventType}) — interim wire types are bucketed (e.g.
 * `claim_suggestion` → Train Me) so filters match EVT-011/012 naming.
 *
 * The 7 canonical V1 types (+ Customer, the 8th slot in the picker) always
 * appear, with `count: 0` when the queue has none — same reasoning as Due's
 * `selected` carve-out: an option that vanishes depending on what happens to
 * be loaded reads as broken, not as "nothing to show here." Only genuinely
 * unknown wire types (`extras` — not in our vocabulary at all) stay
 * presence-gated, since listing every string a backend could ever send would
 * be noise, not a real option.
 */
export function buildTypeOptions(typeCounts = {}, t, { customerCount = 0 } = {}) {
  const displayCounts = Object.entries(typeCounts).reduce((acc, [wireType, count]) => {
    if (TYPE_FILTER_EXCLUDED_WIRE_TYPES.has(wireType)) return acc;
    if (CUSTOMER_WIRE_TYPES.has(wireType)) return acc;
    const display = displayEventType(wireType);
    acc[display] = (acc[display] ?? 0) + count;
    return acc;
  }, {});

  const present = (type) => (displayCounts[type] ?? 0) > 0;
  const known = Object.keys(EVENT_TYPE_META).filter(
    (type) => !CUSTOMER_WIRE_TYPES.has(type) && !TYPE_FILTER_EXCLUDED_WIRE_TYPES.has(type),
  );
  const extras = Object.keys(displayCounts).filter((type) => present(type) && !EVENT_TYPE_META[type]);
  const options = [...known, ...extras].map((type) => {
    const meta = EVENT_TYPE_META[type];
    return {
      value: type,
      label: meta ? t(meta.labelKey) : humanizeReason(type),
      count: displayCounts[type] ?? 0,
    };
  });

  options.push({
    value: TYPE_FILTER_CUSTOMER,
    label: t('filters.customer'),
    count: customerCount,
  });

  return options;
}
export const STUDIO_OPTIONS = [
  { value: 'governance-operations', labelKey: 'studio.governanceOperations' },
  { value: 'agentic-oversight', labelKey: 'studio.agenticOversight' },
];

/** Team filter options (prototype). Wired to `filters.team`; event.team arrives with CT-4867. */
export const TEAM_OPTIONS = [
  { value: 'governance', labelKey: 'team.governance' },
  { value: 'agentic', labelKey: 'team.agentic' },
  { value: 'client', labelKey: 'team.client' },
];

export const TYPE_OPTIONS = [
  { value: EVENT_TYPE.HTL_CONTINUATION, labelKey: 'eventType.htlContinuation' },
  { value: EVENT_TYPE.HTL_HANDOFF, labelKey: 'eventType.htlHandoff' },
  { value: EVENT_TYPE.QUESTION, labelKey: 'eventType.question' },
  { value: EVENT_TYPE.TRAIN_ME, labelKey: 'eventType.trainMe' },
  { value: EVENT_TYPE.GOV_PROMOTION, labelKey: 'eventType.govPromotion' },
  { value: EVENT_TYPE.GOV_REVIEW, labelKey: 'eventType.govReview' },
  { value: EVENT_TYPE.GOV_BREAK_GLASS, labelKey: 'eventType.govBreakGlass' },
  { value: TYPE_FILTER_CUSTOMER, labelKey: 'filters.customer' },
];

export const DUE_BUCKET = {
  OVERDUE: 'overdue',
  DUE_TODAY: 'due_today',
  THIS_WEEK: 'this_week',
  LATER: 'later',
  NO_DATE: 'no_date',
};

export const DUE_OPTIONS = [
  { value: DUE_BUCKET.OVERDUE, labelKey: 'due.overdue' },
  { value: DUE_BUCKET.DUE_TODAY, labelKey: 'due.dueToday' },
  { value: DUE_BUCKET.THIS_WEEK, labelKey: 'due.thisWeek' },
  { value: DUE_BUCKET.LATER, labelKey: 'due.later' },
  { value: DUE_BUCKET.NO_DATE, labelKey: 'due.noDate' },
];

/**
 * Due options built from the buckets actually PRESENT in the queue, in
 * {@link DUE_OPTIONS} order — same reasoning as {@link buildTypeOptions}.
 *
 * Only HITL rows carry a real date (derived from `expiresAt`); every governance
 * row is `no_date` by construction, so the static list above offered "Overdue /
 * Due today / This week" on a queue that had none of them and picking one
 * silently emptied the inbox. A bucket with zero rows is not offered at all —
 * the DS does not render `option.count`, so a "(0)" hint is not available.
 *
 * `overdue` is counted off the cross-cutting `overdue` flag, not off
 * `dueBucket`, matching how the filter itself matches it.
 *
 * `selected` is always kept in the list even at zero rows — resolving the last
 * row of the active bucket must not make the applied filter invisible (and so
 * unclearable) while it still empties the list.
 */
export function buildDueOptions(dueCounts = {}, t, { selected = null } = {}) {
  return DUE_OPTIONS.filter(
    (option) => (dueCounts[option.value] ?? 0) > 0 || option.value === selected,
  ).map((option) => ({ value: option.value, label: t(option.labelKey) }));
}

/** Sort options for the list (default: severity desc, then due date). */
export const SORT_OPTIONS = [
  { value: 'severity', labelKey: 'sort.severity' },
  { value: 'due_date', labelKey: 'sort.dueDate' },
  { value: 'type', labelKey: 'sort.type' },
];
